import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IonPage, IonContent, IonSpinner, useIonRouter, useIonToast } from '@ionic/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useLocation } from 'react-router-dom';
import { personAddOutline, addCircleOutline, peopleOutline, locationOutline } from 'ionicons/icons';
import { useMember, useCreateMember, useUpdateMember } from '@/hooks/useMembers';
import { useSectors } from '@/hooks/useSectors';
import { useFamilies, useCreateFamily, useUpdateFamily } from '@/hooks/useFamilies';
import { useGroups, useAddGroupMember, useRemoveGroupMember } from '@/hooks/useGroups';
import { Gender, MembershipType, ChurchStatus, CivilStatus } from '@/types/member';
import type { MemberFormData, FamilyRole } from '@/types/member';
import { GroupCategory } from '@/types/group';
import {
  GENDER_LABELS, MEMBERSHIP_TYPE_LABELS, CHURCH_STATUS_LABELS,
  CIVIL_STATUS_LABELS, FAMILY_ROLE_LABELS, GROUP_CATEGORY_LABELS, GROUP_CATEGORY_COLORS,
} from '@/utils/labels';
import { usePermission } from '@/hooks/usePermission';
import {
  FormField, FormInput, FormSelect, FormToggle, FormChip,
  FormSection, FormPage, FormTopbar, FormSubmitRow, FormBanner,
  FormGrid, FormInlineCard,
} from '@/components/form';
import AddressAutocomplete from '@/components/shared/AddressAutocomplete';
import type { ParsedAddress } from '@/components/shared/AddressAutocomplete';
import CityAutocomplete from '@/components/shared/CityAutocomplete';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { checkDuplicates, type DuplicateCandidate } from '@/api/members';
import type { AxiosError } from 'axios';
import type { ApiError } from '@/types/api';

/* ─── Schema ─── */
const memberFormSchema = z.object({
  first_name: z.string().min(1, 'Prenumele este obligatoriu'),
  last_name: z.string().min(1, 'Numele este obligatoriu'),
  middle_name: z.string().nullable().optional(),
  gender: z.nativeEnum(Gender, { required_error: 'Genul este obligatoriu' }),
  birth_date: z.string().nullable().optional(),
  birth_place: z.string().nullable().optional(),
  occupation: z.string().nullable().optional(),
  cell_phone: z.string().max(10, 'Maxim 10 caractere').regex(/^(0[237]\d{8})?$/, 'Format invalid (ex: 07xx xxx xxx)').nullable().optional().or(z.literal('')),
  home_phone: z.string().max(10, 'Maxim 10 caractere').regex(/^(0[237]\d{8})?$/, 'Format invalid (ex: 0257 xxx xxx)').nullable().optional().or(z.literal('')),
  email: z.string().email('Email invalid').nullable().optional().or(z.literal('')),
  membership_type: z.nativeEnum(MembershipType),
  church_status: z.nativeEnum(ChurchStatus),
  sector_id: z.string().nullable().optional(),
  blessing_date: z.string().nullable().optional(),
  blessing_place: z.string().nullable().optional(),
  blessing_by: z.string().nullable().optional(),
  water_baptism_date: z.string().nullable().optional(),
  water_baptism_place: z.string().nullable().optional(),
  water_baptism_by: z.string().nullable().optional(),
  spirit_baptism: z.boolean().optional(),
  spirit_baptism_date: z.string().nullable().optional(),
  spirit_baptism_place: z.string().nullable().optional(),
  wedding_date: z.string().nullable().optional(),
  wedding_place: z.string().nullable().optional(),
  wedding_by: z.string().nullable().optional(),
  membership_date: z.string().nullable().optional(),
  membership_place: z.string().nullable().optional(),
  civil_status: z.nativeEnum(CivilStatus).nullable().optional(),
  is_prodigal: z.boolean().optional(),
  has_disability: z.boolean().optional(),
  disability_notes: z.string().nullable().optional(),
  has_special_need: z.boolean().optional(),
  special_need_type: z.string().nullable().optional(),
  special_need_notes: z.string().nullable().optional(),
  no_membership: z.boolean().optional(),
  prodigal_notes: z.string().nullable().optional(),
  spiritual_state: z.string().nullable().optional(),
  material_state: z.string().nullable().optional(),
  health_state: z.string().nullable().optional(),
  special_case: z.string().nullable().optional(),
  family_id: z.string().nullable().optional(),
  family_role: z.enum(['head', 'spouse', 'child', 'other'] as const).nullable().optional(),
  notes: z.string().nullable().optional(),

  // Personal address (used only when member has no family)
  address: z.string().nullable().optional(),
  numar: z.string().nullable().optional(),
  bloc: z.string().nullable().optional(),
  scara: z.string().nullable().optional(),
  etaj: z.string().nullable().optional(),
  apartament: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),

  _create_new_family: z.boolean().optional(),
  _new_family_address: z.string().nullable().optional(),
  _new_family_city: z.string().nullable().optional(),
  _new_family_latitude: z.number().nullable().optional(),
  _new_family_longitude: z.number().nullable().optional(),
}).refine(
  (data) => !data._create_new_family || (data._new_family_address && data._new_family_address.trim().length > 0),
  { message: 'Adresa este obligatorie pentru familie noua', path: ['_new_family_address'] },
);

type MemberFormValues = z.infer<typeof memberFormSchema>;

/* ─── Constants ─── */
const STATE_OPTIONS = [
  { value: 'buna', label: 'Bună' },
  { value: 'medie', label: 'Medie' },
  { value: 'rea', label: 'Rea' },
];

/* ═══════════════════════════════════════════ */

const MemberEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const router = useIonRouter();
  const [present] = useIonToast();
  const { can } = usePermission();
  const canViewSensitive = can('members.view_sensitive');
  // Spiritual-only editors (members.edit_spiritual without members.edit) see
  // and edit nothing but the "Etape Spirituale" section — the API drops every
  // other field from their updates anyway.
  const canEditFull = can('members.edit');

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const prefillFamilyId = searchParams.get('family_id');
  const prefillLastName = searchParams.get('last_name');
  const prefillFamilyRole = searchParams.get('family_role') as FamilyRole | null;
  const prefillSectorId = searchParams.get('sector_id');

  const { data: sectors } = useSectors();
  const { data: familiesData } = useFamilies({ page_size: 500 });
  const familiesList = familiesData?.data ?? [];
  const familyOptions = useMemo(() =>
    familiesList.map((f) => ({ value: f.id, label: f.display_name ?? f.name })),
    [familiesList],
  );

  const isEditing = !!id && id !== 'new';

  // Creating people requires the full edit bundle — bounce spiritual-only
  // editors who land on the "new" form back to the list.
  useEffect(() => {
    if (!isEditing && !canEditFull) router.push('/members', 'back', 'replace');
  }, [isEditing, canEditFull, router]);

  const { data: existingMember, isLoading: memberLoading } = useMember(isEditing ? id : undefined);
  const createMutation = useCreateMember();
  const updateMutation = useUpdateMember();
  const createFamilyMutation = useCreateFamily();
  const updateFamilyMutation = useUpdateFamily();

  const defaultGender = prefillFamilyRole === 'spouse' ? Gender.FEMALE : Gender.MALE;
  // New people default to "aparținător" (dependent); the user promotes to
  // "membru" explicitly when applicable.
  const defaultMembershipType = MembershipType.DEPENDENT;

  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isDirty } } = useForm<MemberFormValues>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: {
      first_name: '', last_name: prefillLastName ?? '', middle_name: null,
      gender: defaultGender, birth_date: null, birth_place: null, occupation: null,
      cell_phone: null, home_phone: null, email: null,
      membership_type: defaultMembershipType, church_status: ChurchStatus.ACTIVE,
      sector_id: prefillSectorId ?? null,
      blessing_date: null, blessing_place: null, blessing_by: null,
      water_baptism_date: null, water_baptism_place: null, water_baptism_by: null,
      spirit_baptism: false, spirit_baptism_date: null, spirit_baptism_place: null,
      wedding_date: null, wedding_place: null, wedding_by: null, civil_status: null,
      is_prodigal: false,
      has_disability: false, disability_notes: null,
      has_special_need: false, special_need_type: null, special_need_notes: null,
      no_membership: false, prodigal_notes: null,
      spiritual_state: null, material_state: null, health_state: null,
      special_case: null,
      family_id: prefillFamilyId ?? null, family_role: prefillFamilyRole ?? null,
      notes: null,
      address: null, numar: null, bloc: null, scara: null, etaj: null, apartament: null,
      city: null, latitude: null, longitude: null,
      _create_new_family: false, _new_family_address: null, _new_family_city: null,
      _new_family_latitude: null, _new_family_longitude: null,
    },
  });

  const w = {
    spiritBaptism: watch('spirit_baptism'),
    isProdigal: watch('is_prodigal'),
    hasDisability: watch('has_disability'),
    hasSpecialNeed: watch('has_special_need'),
    familyId: watch('family_id'),
    createNewFamily: watch('_create_new_family'),
    familyRole: watch('family_role'),
    waterBaptismDate: watch('water_baptism_date'),
  };

  useEffect(() => {
    if (w.familyRole === 'head' && !w.familyId && !isEditing) setValue('_create_new_family', true);
  }, [w.familyRole, w.familyId, isEditing, setValue]);

  useEffect(() => {
    if (!existingMember) return;
    // When editing the head of a family, surface the family's address in the
    // address form (the member's own address fields are blank for family members).
    const isHead = !!existingMember.family_id && existingMember.family_role === 'head';
    const addressOverrides = isHead
      ? {
          address: existingMember.effective_address,
          numar: existingMember.effective_numar,
          bloc: existingMember.effective_bloc,
          scara: existingMember.effective_scara,
          etaj: existingMember.effective_etaj,
          apartament: existingMember.effective_apartament,
          city: existingMember.effective_city,
          latitude: existingMember.effective_latitude,
          longitude: existingMember.effective_longitude,
        }
      : {};
    reset({ ...existingMember, ...addressOverrides, _create_new_family: false, _new_family_address: null, _new_family_city: null, _new_family_latitude: null, _new_family_longitude: null } as MemberFormValues);
  }, [existingMember, reset]);

  // ── Groups ──
  const { data: groupsData } = useGroups();
  const allGroups = groupsData?.data ?? [];
  const addGroupMemberMut = useAddGroupMember();
  const removeGroupMemberMut = useRemoveGroupMember();
  const [memberGroupIds, setMemberGroupIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (existingMember?.groups) {
      setMemberGroupIds(new Set(existingMember.groups.map((g: { id: string }) => g.id)));
    }
  }, [existingMember]);

  const toggleGroup = async (groupId: string) => {
    const inGroup = memberGroupIds.has(groupId);
    setMemberGroupIds((prev) => {
      const next = new Set(prev);
      if (inGroup) next.delete(groupId); else next.add(groupId);
      return next;
    });
    // For existing members, update immediately via API
    if (isEditing && id) {
      try {
        if (inGroup) {
          await removeGroupMemberMut.mutateAsync({ groupId, memberId: id });
          present({ message: 'Eliminat din grup.', duration: 2000, color: 'success', position: 'bottom' });
        } else {
          await addGroupMemberMut.mutateAsync({ groupId, data: { member_id: id, role: 'member' } });
          present({ message: 'Adăugat în grup.', duration: 2000, color: 'success', position: 'bottom' });
        }
      } catch {
        setMemberGroupIds((prev) => {
          const next = new Set(prev);
          if (inGroup) next.add(groupId); else next.delete(groupId);
          return next;
        });
        present({ message: 'Eroare la actualizarea grupului.', duration: 2000, color: 'danger', position: 'bottom' });
      }
    }
    // For new members, groups are stored locally and attached after save
  };

  // ── Duplicate name warning (soft, non-blocking) ──
  const [duplicateCandidates, setDuplicateCandidates] = useState<DuplicateCandidate[]>([]);
  const dupTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const watchedFirstName = watch('first_name');
  const watchedLastName = watch('last_name');

  useEffect(() => {
    if (isEditing) return;
    clearTimeout(dupTimerRef.current);
    if ((watchedFirstName?.length ?? 0) < 2 || (watchedLastName?.length ?? 0) < 2) {
      setDuplicateCandidates([]);
      return;
    }
    dupTimerRef.current = setTimeout(async () => {
      try {
        const results = await checkDuplicates({
          first_name: watchedFirstName,
          last_name: watchedLastName,
        });
        setDuplicateCandidates(results);
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(dupTimerRef.current);
  }, [watchedFirstName, watchedLastName, isEditing]);

  const onSubmit = async (data: MemberFormValues) => {
    let familyId = data.family_id;
    if (data._create_new_family && !isEditing) {
      try {
        const f = await createFamilyMutation.mutateAsync({ name: data.last_name, address: data._new_family_address || null, city: data._new_family_city || null, latitude: data._new_family_latitude ?? null, longitude: data._new_family_longitude ?? null, sector_id: data.sector_id || null });
        familyId = f.id;
      } catch { present({ message: 'Eroare la crearea familiei.', duration: 3000, color: 'danger', position: 'bottom' }); return; }
    }
    const cleaned = Object.fromEntries(Object.entries(data).filter(([k]) => !k.startsWith('_')).map(([k, v]) => [k, v === '' ? null : v])) as unknown as MemberFormData;
    cleaned.family_id = familyId || null;

    // When editing the head of a family, the address belongs to the family.
    // The backend ignores personal address fields for members in a family,
    // so persist them via the family update instead. Spiritual-only editors
    // never touch addresses (and lack families.edit), so skip it for them.
    const isHead = canEditFull && isEditing && !!familyId && data.family_role === 'head';

    try {
      if (isEditing) {
        if (isHead && familyId) {
          await updateFamilyMutation.mutateAsync({
            id: familyId,
            data: {
              address: data.address || null,
              numar: data.numar || null,
              bloc: data.bloc || null,
              scara: data.scara || null,
              etaj: data.etaj || null,
              apartament: data.apartament || null,
              city: data.city || null,
              latitude: data.latitude ?? null,
              longitude: data.longitude ?? null,
            },
          });
        }
        await updateMutation.mutateAsync({ id, data: cleaned });
        present({ message: 'Persoana a fost actualizată.', duration: 2000, color: 'success', position: 'bottom' });
        router.goBack();
      } else {
        const m = await createMutation.mutateAsync(cleaned);
        // Attach selected groups to the newly created member
        if (memberGroupIds.size > 0) {
          for (const groupId of memberGroupIds) {
            try {
              await addGroupMemberMut.mutateAsync({ groupId, data: { member_id: m.id, role: 'member' } });
            } catch { /* ignore individual failures */ }
          }
        }
        present({ message: `${m.first_name} ${m.last_name} a fost adăugat(ă).`, duration: 2000, color: 'success', position: 'bottom' });
        if (prefillFamilyId) router.push(`/families/${prefillFamilyId}`, 'back', 'replace');
        else router.push(`/members/${m.id}`, 'forward', 'replace');
      }
    } catch (err) {
      const axiosErr = err as AxiosError<ApiError>;
      const serverErrors = axiosErr?.response?.data?.errors;
      if (serverErrors) {
        const firstMsg = Object.values(serverErrors).flat()[0];
        present({ message: firstMsg || 'Eroare la salvare.', duration: 5000, color: 'danger', position: 'bottom' });
      } else {
        present({ message: 'Eroare la salvare.', duration: 3000, color: 'danger', position: 'bottom' });
      }
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending || createFamilyMutation.isPending || updateFamilyMutation.isPending;
  const currentFamily = familiesList.find((f) => f.id === w.familyId);

  // The head of a family edits the family's address directly from this form.
  const editsFamilyAddress = isEditing && !!w.familyId && w.familyRole === 'head' && !w.createNewFamily;

  if (isEditing && memberLoading) return <IonPage><IonContent><div className="form-loading"><IonSpinner name="crescent" /></div></IonContent></IonPage>;

  return (
    <IonPage>
      <IonContent>
        <FormPage>
          <FormTopbar
            title={isEditing ? 'Editare Persoană' : 'Persoană Nouă'}
            icon={personAddOutline}
            onBack={() => router.goBack()}
            onSave={handleSubmit(onSubmit)}
            saving={isPending}
            disabled={!isDirty && isEditing}
          />

          {prefillFamilyId && !isEditing && (
            <FormBanner icon={peopleOutline} title={`Adăugare la Familia ${prefillLastName}`}
              subtitle={`Rol: ${FAMILY_ROLE_LABELS[prefillFamilyRole ?? ''] ?? 'Membru'}`} />
          )}

          <form onSubmit={handleSubmit(onSubmit)}>

            {/* ═══ IDENTITATE ═══ */}
            {canEditFull && (
            <FormSection title="Identitate">
              <FormGrid>
                <Controller control={control} name="last_name" render={({ field }) => (
                  <FormField label="Nume" required error={errors.last_name?.message}>
                    <FormInput value={field.value} onChange={(e) => field.onChange(e.currentTarget.value)} placeholder="ex: Popescu" />
                  </FormField>
                )} />
                <Controller control={control} name="first_name" render={({ field }) => (
                  <FormField label="Prenume" required error={errors.first_name?.message}>
                    <FormInput value={field.value} onChange={(e) => field.onChange(e.currentTarget.value)} placeholder="ex: Ioan" />
                  </FormField>
                )} />
                <Controller control={control} name="middle_name" render={({ field }) => (
                  <FormField label="Al doilea prenume">
                    <FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} />
                  </FormField>
                )} />
                <Controller control={control} name="gender" render={({ field }) => (
                  <FormField label="Gen" required error={errors.gender?.message}>
                    <FormSelect value={field.value} onChange={(e) => field.onChange(e.currentTarget.value)}>
                      {Object.values(Gender).map((g) => <option key={g} value={g}>{GENDER_LABELS[g]}</option>)}
                    </FormSelect>
                  </FormField>
                )} />
                <Controller control={control} name="birth_date" render={({ field }) => (
                  <FormField label="Data nașterii"><FormInput type="date" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="birth_place" render={({ field }) => (
                  <FormField label="Locul nașterii">
                    <CityAutocomplete value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} placeholder="ex: Arad" />
                  </FormField>
                )} />
                <Controller control={control} name="occupation" render={({ field }) => (
                  <FormField label="Ocupație"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="civil_status" render={({ field }) => (
                  <FormField label="Stare civilă">
                    <FormSelect value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value || null)}>
                      <option value="">Nespecificat</option>
                      {Object.values(CivilStatus).map((cs) => <option key={cs} value={cs}>{CIVIL_STATUS_LABELS[cs]}</option>)}
                    </FormSelect>
                  </FormField>
                )} />
              </FormGrid>

              {!isEditing && duplicateCandidates.length > 0 && (
                <div className="duplicate-warning">
                  <strong>Atenție!</strong> Persoane cu nume similar există deja:
                  <ul>
                    {duplicateCandidates.map((d) => (
                      <li key={d.id}>
                        <a href={`/members/${d.id}`} target="_blank" rel="noreferrer">
                          {d.last_name} {d.first_name}{d.middle_name ? ` ${d.middle_name}` : ''}
                        </a>
                        {d.birth_date ? ` · ${d.birth_date}` : ''}
                        {d.cell_phone ? ` · ${d.cell_phone}` : ''}
                        {d.membership_type ? ` · ${d.membership_type}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </FormSection>
            )}

            {/* ═══ CONTACT ═══ */}
            {canEditFull && (
            <FormSection title="Contact">
              <FormGrid>
                <Controller control={control} name="cell_phone" render={({ field }) => (
                  <FormField label="Telefon mobil" error={errors.cell_phone?.message}><FormInput type="tel" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value.replace(/\D/g, '').slice(0, 10))} placeholder="07xx xxx xxx" maxLength={10} /></FormField>
                )} />
                <Controller control={control} name="home_phone" render={({ field }) => (
                  <FormField label="Telefon fix" error={errors.home_phone?.message}><FormInput type="tel" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value.replace(/\D/g, '').slice(0, 10))} placeholder="0257 xxx xxx" maxLength={10} /></FormField>
                )} />
                <Controller control={control} name="email" render={({ field }) => (
                  <FormField label="Email" error={errors.email?.message}>
                    <FormInput type="email" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} />
                  </FormField>
                )} />
              </FormGrid>
            </FormSection>
            )}

            {/* ═══ FAMILIE ═══ */}
            {canEditFull && (
            <FormSection title="Familie">
              <FormGrid>
                {!w.createNewFamily && (
                  <Controller control={control} name="family_id" render={({ field }) => (
                    <FormField label="Familie">
                      <SearchableSelect
                        options={familyOptions}
                        value={field.value ?? null}
                        onChange={(val) => field.onChange(val)}
                        placeholder="Caută familie..."
                        emptyLabel="Niciuna"
                      />
                    </FormField>
                  )} />
                )}
                <Controller control={control} name="family_role" render={({ field }) => (
                  <FormField label="Rol în familie">
                    <FormSelect value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value || null)}>
                      <option value="">Nespecificat</option>
                      {(['head', 'spouse', 'child', 'other'] as FamilyRole[]).map((r) => <option key={r} value={r}>{FAMILY_ROLE_LABELS[r]}</option>)}
                    </FormSelect>
                  </FormField>
                )} />
              </FormGrid>

              {!w.familyId && !isEditing && (
                <div style={{ marginTop: 12 }}>
                  <Controller control={control} name="_create_new_family" render={({ field }) => (
                    <FormToggle label="Creează familie nouă" checked={!!field.value}
                      onChange={(v) => { field.onChange(v); if (!v) { setValue('_new_family_address', null); setValue('_new_family_city', null); setValue('_new_family_latitude', null); setValue('_new_family_longitude', null); } }} />
                  )} />
                  {w.createNewFamily && (
                    <FormInlineCard note={`Se va crea familia "${watch('last_name') || '...'}"`} icon={addCircleOutline} style={{ marginTop: 8 }}>
                      <FormField label="Adresa familiei *">
                        <AddressAutocomplete
                          value={[watch('_new_family_address'), watch('_new_family_city')].filter(Boolean).join(', ')}
                          placeholder="Scrie adresa sau cauta..."
                          onSelect={(result: ParsedAddress) => {
                            setValue('_new_family_address', result.address, { shouldDirty: true });
                            setValue('_new_family_city', result.city, { shouldDirty: true });
                            setValue('_new_family_latitude', result.latitude, { shouldDirty: true });
                            setValue('_new_family_longitude', result.longitude, { shouldDirty: true });
                          }}
                          onClear={() => {
                            setValue('_new_family_address', null, { shouldDirty: true });
                            setValue('_new_family_city', null, { shouldDirty: true });
                            setValue('_new_family_latitude', null, { shouldDirty: true });
                            setValue('_new_family_longitude', null, { shouldDirty: true });
                          }}
                          onManualChange={(val) => {
                            setValue('_new_family_address', val, { shouldDirty: true });
                          }}
                        />
                      </FormField>
                    </FormInlineCard>
                  )}
                </div>
              )}

              {currentFamily && currentFamily.member_count > 0 && (
                <FormInlineCard note={`Membri ai familiei ${currentFamily.name}:`} style={{ marginTop: 12 }}>
                  {[...(currentFamily.members ?? [])]
                    .sort((a, b) => {
                      const order: Record<string, number> = { head: 0, spouse: 1, child: 2, other: 3 };
                      return (order[a.family_role ?? 'other'] ?? 9) - (order[b.family_role ?? 'other'] ?? 9);
                    })
                    .map((m) => (
                      <div key={m.id} style={{ fontSize: 13, color: '#475569', padding: '2px 0' }}>
                        {(m.full_name || `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || '—')} — {FAMILY_ROLE_LABELS[m.family_role ?? ''] ?? 'Membru'}
                      </div>
                    ))}
                </FormInlineCard>
              )}
            </FormSection>
            )}

            {/* ═══ ADRESĂ (membri fără familie sau capul familiei) ═══ */}
            {canEditFull && ((!w.familyId && !w.createNewFamily) || editsFamilyAddress) && (
              <FormSection title={editsFamilyAddress ? 'Adresa familiei' : 'Adresă'}>
                <FormInlineCard
                  icon={locationOutline}
                  note={editsFamilyAddress
                    ? 'Aceasta este adresa familiei. Modificările se aplică întregii familii.'
                    : 'Persoanele fără familie asociată primesc adresă proprie. Dacă adaugi ulterior persoana într-o familie, adresa familiei devine cea valabilă.'}
                  style={{ marginBottom: 12 }}
                >
                  <div></div>
                </FormInlineCard>

                <FormGrid cols={2}>
                  <FormField label="Adresa">
                    <AddressAutocomplete
                      value={[watch('address'), watch('city')].filter(Boolean).join(', ')}
                      placeholder="Scrie adresa sau cauta..."
                      onSelect={(result: ParsedAddress) => {
                        setValue('address', result.address || result.displayName, { shouldDirty: true });
                        setValue('city', result.city || '', { shouldDirty: true });
                        setValue('latitude', result.latitude, { shouldDirty: true });
                        setValue('longitude', result.longitude, { shouldDirty: true });
                      }}
                      onClear={() => {
                        setValue('address', null, { shouldDirty: true });
                        setValue('city', null, { shouldDirty: true });
                        setValue('latitude', null, { shouldDirty: true });
                        setValue('longitude', null, { shouldDirty: true });
                      }}
                      onManualChange={(val) => {
                        setValue('address', val, { shouldDirty: true });
                      }}
                    />
                  </FormField>
                </FormGrid>

                <FormGrid cols={6} style={{ marginTop: 12 }}>
                  <Controller control={control} name="numar" render={({ field }) => (
                    <FormField label="Nr."><FormInput value={field.value ?? ''} placeholder="15" onChange={(e) => field.onChange(e.currentTarget.value || null)} /></FormField>
                  )} />
                  <Controller control={control} name="bloc" render={({ field }) => (
                    <FormField label="Bloc"><FormInput value={field.value ?? ''} placeholder="B2" onChange={(e) => field.onChange(e.currentTarget.value || null)} /></FormField>
                  )} />
                  <Controller control={control} name="scara" render={({ field }) => (
                    <FormField label="Scara"><FormInput value={field.value ?? ''} placeholder="A" onChange={(e) => field.onChange(e.currentTarget.value || null)} /></FormField>
                  )} />
                  <Controller control={control} name="etaj" render={({ field }) => (
                    <FormField label="Etaj"><FormInput value={field.value ?? ''} placeholder="3" onChange={(e) => field.onChange(e.currentTarget.value || null)} /></FormField>
                  )} />
                  <Controller control={control} name="apartament" render={({ field }) => (
                    <FormField label="Ap."><FormInput value={field.value ?? ''} placeholder="12" onChange={(e) => field.onChange(e.currentTarget.value || null)} /></FormField>
                  )} />
                  <Controller control={control} name="city" render={({ field }) => (
                    <FormField label="Oraș"><FormInput value={field.value ?? ''} placeholder="Arad" onChange={(e) => field.onChange(e.currentTarget.value || null)} /></FormField>
                  )} />
                </FormGrid>
              </FormSection>
            )}

            {/* ═══ STATUT BISERICESC ═══ */}
            {canEditFull && (
            <FormSection title="Statut Bisericesc">
              <FormGrid>
                <Controller control={control} name="membership_type" render={({ field }) => (
                  <FormField label="Tip" required>
                    <FormSelect value={field.value} onChange={(e) => field.onChange(e.currentTarget.value)}>
                      {Object.values(MembershipType).map((mt) => <option key={mt} value={mt}>{MEMBERSHIP_TYPE_LABELS[mt]}</option>)}
                    </FormSelect>
                  </FormField>
                )} />
                <Controller control={control} name="church_status" render={({ field }) => (
                  <FormField label="Status" required>
                    <FormSelect value={field.value} onChange={(e) => field.onChange(e.currentTarget.value)}>
                      {Object.values(ChurchStatus).map((cs) => <option key={cs} value={cs}>{CHURCH_STATUS_LABELS[cs]}</option>)}
                    </FormSelect>
                  </FormField>
                )} />
                <Controller control={control} name="sector_id" render={({ field }) => (
                  <FormField label="Sector">
                    <FormSelect value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value || null)}>
                      <option value="">Nealocat</option>
                      {(sectors ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </FormSelect>
                  </FormField>
                )} />
              </FormGrid>
            </FormSection>
            )}

            {/* ═══ ETAPE SPIRITUALE ═══ */}
            <FormSection title="Etape Spirituale" collapsible defaultOpen={isEditing}>
              <FormGrid>
                <Controller control={control} name="blessing_date" render={({ field }) => (
                  <FormField label="Binecuvântare — Data"><FormInput type="date" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="blessing_place" render={({ field }) => (
                  <FormField label="Binecuvântare — Locul"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="blessing_by" render={({ field }) => (
                  <FormField label="Binecuvântare — Slujitori"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="water_baptism_date" render={({ field }) => (
                  <FormField label="Botez în apă — Data"><FormInput type="date" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="water_baptism_place" render={({ field }) => (
                  <FormField label="Botez în apă — Locul"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="water_baptism_by" render={({ field }) => (
                  <FormField label="Botez în apă — Slujitori"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="wedding_date" render={({ field }) => (
                  <FormField label="Căsătorie — Data"><FormInput type="date" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="wedding_place" render={({ field }) => (
                  <FormField label="Căsătorie — Locul"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="wedding_by" render={({ field }) => (
                  <FormField label="Căsătorie — Slujitori"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                )} />
                <Controller control={control} name="membership_date" render={({ field }) => (
                  <FormField label="Primire ca membru — Data">
                    <FormInput type="date" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} />
                    {w.waterBaptismDate && w.waterBaptismDate !== field.value && (
                      <button
                        type="button"
                        className="form-field__quick-fill"
                        onClick={() => setValue('membership_date', w.waterBaptismDate!, { shouldDirty: true, shouldValidate: true })}
                        title="Folosește aceeași dată cu botezul în apă"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        <span>La fel cu data botezului</span>
                      </button>
                    )}
                  </FormField>
                )} />
                <Controller control={control} name="membership_place" render={({ field }) => (
                  <FormField label="Primire ca membru — De unde" span={2}><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} placeholder="ex: Betel Arad, Biserica Baptistă Speranța" /></FormField>
                )} />
              </FormGrid>
              <div style={{ marginTop: 12 }}>
                <Controller control={control} name="spirit_baptism" render={({ field }) => (
                  <FormToggle label="Botezat cu Duhul Sfânt" checked={!!field.value} onChange={field.onChange} />
                )} />
                {w.spiritBaptism && (
                  <FormGrid style={{ marginTop: 8 }}>
                    <Controller control={control} name="spirit_baptism_date" render={({ field }) => (
                      <FormField label="Data"><FormInput type="date" value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                    )} />
                    <Controller control={control} name="spirit_baptism_place" render={({ field }) => (
                      <FormField label="Locul"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                    )} />
                  </FormGrid>
                )}
              </div>
            </FormSection>

            {/* ═══ ETICHETE ═══ */}
            {canEditFull && (
            <FormSection title="Etichete" collapsible defaultOpen={isEditing}>
              <div className="form-toggles-row">
                <Controller control={control} name="is_prodigal" render={({ field }) => <FormToggle label="Fiu Risipitor" checked={!!field.value} onChange={field.onChange} />} />
                <Controller control={control} name="has_disability" render={({ field }) => <FormToggle label="Dizabilitate" checked={!!field.value} onChange={field.onChange} />} />
                <Controller control={control} name="has_special_need" render={({ field }) => <FormToggle label="Nevoie Specială" checked={!!field.value} onChange={field.onChange} />} />
                <Controller control={control} name="no_membership" render={({ field }) => <FormToggle label="Fără Membralitate" checked={!!field.value} onChange={field.onChange} />} />
              </div>
              {w.isProdigal && (
                <FormGrid style={{ marginTop: 8 }}>
                  <Controller control={control} name="prodigal_notes" render={({ field }) => (
                    <FormField label="Detalii fiu risipitor" span={2}><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                  )} />
                </FormGrid>
              )}
              {w.hasDisability && (
                <FormGrid style={{ marginTop: 8 }}>
                  <Controller control={control} name="disability_notes" render={({ field }) => (
                    <FormField label="Detalii dizabilitate" span={2}><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                  )} />
                </FormGrid>
              )}
              {w.hasSpecialNeed && (
                <FormGrid style={{ marginTop: 8 }}>
                  <Controller control={control} name="special_need_type" render={({ field }) => (
                    <FormField label="Tip nevoie"><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                  )} />
                  <Controller control={control} name="special_need_notes" render={({ field }) => (
                    <FormField label="Detalii" span={2}><FormInput value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value)} /></FormField>
                  )} />
                </FormGrid>
              )}
            </FormSection>
            )}

            {/* ═══ DATE SENSIBILE (admin/pastor) ═══ */}
            {canEditFull && canViewSensitive && (
              <FormSection title="Date Sensibile" collapsible defaultOpen={isEditing}>
                <FormGrid>
                  <Controller control={control} name="material_state" render={({ field }) => (
                    <FormField label="Stare materială">
                      <FormSelect value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value || null)}>
                        <option value="">Nespecificat</option>
                        {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </FormSelect>
                    </FormField>
                  )} />
                  <Controller control={control} name="health_state" render={({ field }) => (
                    <FormField label="Stare sănătate">
                      <FormSelect value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value || null)}>
                        <option value="">Nespecificat</option>
                        {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </FormSelect>
                    </FormField>
                  )} />
                  <Controller control={control} name="spiritual_state" render={({ field }) => (
                    <FormField label="Stare spirituală">
                      <FormSelect value={field.value ?? ''} onChange={(e) => field.onChange(e.currentTarget.value || null)}>
                        <option value="">Nespecificat</option>
                        {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </FormSelect>
                    </FormField>
                  )} />
                  <Controller control={control} name="special_case" render={({ field }) => (
                    <FormField label="Caz special" span={3}>
                      <textarea className="form-textarea" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)} rows={2} placeholder="Observații cazuri speciale..." />
                    </FormField>
                  )} />
                </FormGrid>
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <label className="form-field__label" style={{ margin: 0 }}>Grupuri / Implicare în biserică</label>
                    {isEditing && <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>(se salvează automat)</span>}
                  </div>
                  {allGroups.length > 0 ? (
                    Object.values(GroupCategory).map((cat) => {
                      const catGroups = allGroups.filter((g) => g.category === cat);
                      if (catGroups.length === 0) return null;
                      return (
                        <div key={cat} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: GROUP_CATEGORY_COLORS[cat] ?? '#94a3b8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {GROUP_CATEGORY_LABELS[cat]}
                          </div>
                          <div className="form-chips">
                            {catGroups.map((g) => (
                              <FormChip key={g.id} label={g.name} selected={memberGroupIds.has(g.id)} onClick={() => toggleGroup(g.id)} />
                            ))}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p style={{ fontSize: 13, color: '#94a3b8' }}>Nu există grupuri definite.</p>
                  )}
                </div>
              </FormSection>
            )}

            {/* ═══ OBSERVAȚII ═══ */}
            {canEditFull && (
            <FormSection title="Observații" collapsible defaultOpen={isEditing}>
              <Controller control={control} name="notes" render={({ field }) => (
                <textarea className="form-textarea" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)} rows={3} placeholder="Observații generale..." />
              )} />
            </FormSection>
            )}

            <FormSubmitRow
              onCancel={() => router.goBack()}
              submitLabel={isEditing ? 'Actualizează' : w.createNewFamily ? 'Creează Persoană + Familie' : 'Creează Persoană'}
              saving={isPending}
            />
          </form>
        </FormPage>

        {/* ── Saving overlay ── */}
        {isPending && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.7)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(2px)',
          }}>
            <IonSpinner name="crescent" style={{ width: 36, height: 36, color: '#5e81f4' }} />
            <p style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: '#475569' }}>
              {isEditing ? 'Se actualizează...' : 'Se creează...'}
            </p>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default MemberEditPage;
